import { useCallback, useContext } from 'react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { noop } from 'lodash-es';

import type { User } from '@hotjar/user';
import { useOrganization } from '@hotjar/organization';
import { useConfirmation } from './useConfirmation';

import type { CommentCreatorProps } from '../components/CommentCreator';
import { CommentCreator } from '../components/CommentCreator';
import { CommentContent } from '../components/CommentContent';
import type { ThreadProps } from '../components/Thread';
import type { RemovalConfirmationProps } from '../components/RemovalConfirmation';
import { parse, resolveMentionDetails, stringify } from '../utils/content-utils';
import type { CommentTemplate, ContentToken, Thread, Reply, WidgetType } from '../domains/model';
import type { ScopeId } from '../domains/api/types';
import { useCommentsApi, STATUS } from './useCommentsApi';
import { LegacyContext } from '../context/global-context';

export enum ItemType {
  HIGHLIGHT = 'highlight',
}

export interface TimelineActiveItem {
  type: ItemType;
  id: number;
}

export interface UseCommentsProps {
  allowThreadRemoval?: boolean;
  organizationId: number;
  currentUser: User;
  siteId: string | null;
  widgetId: number;
  widgetType: WidgetType;
  scopeId: ScopeId;
  apiToken: string | null;
  onThreadAdded: (thread: Thread) => void;
  onThreadRemoved?: (thread: Thread) => void;
  onReplyAdded?: (reply: Reply) => void;
  onReplyRemoved?: (reply: Reply) => void;
  newThreadMetadata: { time: number } | Record<string, unknown>;
  compactNewThread?: boolean;
  autoFocusTextField?: boolean;
  isPlaying?: boolean;
  onPause?: (showAnimation: boolean, isManual: boolean) => void;
  setActiveItem: (item: TimelineActiveItem | null) => void;
  invalidateQuery: {
    onPut: () => void;
    onPost: () => void;
    onPostReply: () => void;
    onRemoveSetThreads: () => void;
    onRemove: () => void;
  };
}

interface UseCommentsHook {
  areThreadsFetched: boolean;
  threads: Array<Thread>;
  threadProps: Omit<ThreadProps, 'thread' | 'onClose'>;
  removalConfirmationProps: RemovalConfirmationProps;
  renderNewThread: () => React.ReactNode;
}

export function useComments({
  allowThreadRemoval = true,
  organizationId,
  currentUser,
  onPause = () => {
    /* noop */
  },
  isPlaying,
  siteId,
  widgetId,
  widgetType,
  scopeId,
  apiToken,
  onThreadAdded,
  onThreadRemoved,
  onReplyAdded = noop,
  onReplyRemoved = noop,
  newThreadMetadata,
  autoFocusTextField = false,
  setActiveItem,
  invalidateQuery,
}: UseCommentsProps): UseCommentsHook {
  const { tagRecording, useAuthorization = () => ({ hasPermission: () => false }) } =
    useContext(LegacyContext);

  const [{ threads, status }, { put, remove, post, postReply }] = useCommentsApi({
    siteId,
    widgetId,
    widgetType,
    scopeId,
    token: apiToken,
    invalidateQuery,
  });

  const { data: organization } = useOrganization(organizationId, currentUser.id);
  const { hasPermission } = useAuthorization();
  const hasOrgInvitePermission = hasPermission(
    'organization',
    organization?.id ? `${organization.id}` : null,
    'org-invite',
  );

  const onInteractionDoPause = useCallback(() => {
    if (isPlaying) {
      onPause(false, false);
    }
  }, [isPlaying, onPause]);

  const {
    scope: thingToRemove,
    confirm: confirmRemoval,
    abort: abortRemoval,
    handle: handleRemoval,
  } = useConfirmation({
    onConfirm: async (currentScope: Thread | Reply) => {
      await remove(currentScope);

      if ('widgetId' in currentScope) {
        onThreadRemoved?.(currentScope);
        return;
      }

      onReplyRemoved(currentScope);
    },
  });
  const isRemovalConfirmationVisible = thingToRemove !== null;
  const { t } = useTranslation();

  const organizationName = organization?.name ?? '';
  const users = organization?.users ?? [];

  function renderCommentCreator({
    saveButtonText,
    inputPlaceholder,
    inlineButton,
    onSave,
    additionalButtons,
    initialContent = [],
    autofocus,
  }: Pick<
    CommentCreatorProps,
    'saveButtonText' | 'inputPlaceholder' | 'additionalButtons' | 'inlineButton'
  > & {
    onSave: (commentTemplate: CommentTemplate) => Promise<void>;
    initialContent?: Array<ContentToken>;
    autofocus?: boolean;
  }): React.ReactNode {
    return (
      <CommentCreator
        initialContent={stringify(initialContent)}
        organizationName={organizationName}
        saveButtonText={saveButtonText}
        inputPlaceholder={inputPlaceholder}
        inlineButton={inlineButton}
        users={users}
        onSave={(content) => {
          const [text, mentions] = content;
          onSave({
            author: currentUser,
            content: parse(text, resolveMentionDetails(mentions, users)),
          }).catch(() => undefined);
        }}
        onInteraction={onInteractionDoPause}
        apiStatus={status}
        additionalButtons={additionalButtons}
        autofocus={autofocus || autoFocusTextField}
        hasOrgInvitePermission={hasOrgInvitePermission}
      />
    );
  }

  return {
    areThreadsFetched: status === STATUS.SUCCESS,
    threads,
    threadProps: {
      renderComment: (comment, handleClose) => {
        const isReply = 'threadId' in comment;

        return (
          <CommentContent
            currentUserId={currentUser.id}
            onDelete={isReply || allowThreadRemoval ? () => handleRemoval(comment) : undefined}
            onClose={handleClose}
            onShowMoreMenu={onInteractionDoPause}
            comment={comment}
            renderCreator={(onSave, editedComment, additionalButtons) =>
              renderCommentCreator({
                saveButtonText: t('comment:update.confirm'),
                inputPlaceholder: t('comment:update.placeholder'),
                additionalButtons,
                autofocus: true,
                inlineButton: false,
                initialContent: editedComment.content,
                onSave: async (commentTemplate) => {
                  onSave();
                  await put({ ...editedComment, ...commentTemplate });
                },
              })
            }
          />
        );
      },
      renderCreator: (threadId) =>
        renderCommentCreator({
          saveButtonText: t('comment:reply.action'),
          inputPlaceholder: t('comment:reply.placeholder'),
          inlineButton: true,
          onSave: async (commentTemplate) => {
            const reply = await postReply({ ...commentTemplate, threadId });

            onReplyAdded(reply);

            if (tagRecording) {
              tagRecording('comment_added');
            }
          },
        }),
    },
    renderNewThread: () =>
      renderCommentCreator({
        saveButtonText: t('comment:create.confirm'),
        inputPlaceholder: t('comment:create.placeholder'),
        inlineButton: true,
        onSave: async (commentTemplate) => {
          const newThread = await post({
            ...commentTemplate,
            widgetId,
            metadata: newThreadMetadata,
          });
          setActiveItem({ type: ItemType.HIGHLIGHT, id: newThread.id });

          onThreadAdded(newThread);

          if (tagRecording) {
            tagRecording('comment_added');
          }
        },
      }),
    removalConfirmationProps: {
      isThread: thingToRemove !== null && 'replies' in thingToRemove,
      isVisible: isRemovalConfirmationVisible,
      onConfirm: confirmRemoval,
      onAbort: abortRemoval,
    },
  };
}
